import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import AgentLayout from "../components/layout/AgentLayout";
import PageHeader from "../components/common/PageHeader";
import { DASHBOARD_CRUMB } from "../components/common/Breadcrumb";
import { agentApi } from "../lib/apiClient";
import { showApiError } from "../lib/alerts";
import { asArray, unwrapData, appendUniqueById, readPagination } from "../lib/catalog";
import { CatalogSkeletonGrid } from "../components/catalog/CatalogSkeletons";
import HotelCard from "../components/hotels/HotelCard";
import HotelFilterBar from "../components/hotels/HotelFilterBar";
import {
  countHotelFilters,
  emptyHotelFilters,
  hotelMatchesFilters,
} from "../components/hotels/hotelCatalog";

const PAGE_SIZE = 15;

export default function HotelsPage() {
  const [searchParams] = useSearchParams();
  const [hotels, setHotels] = useState([]);
  const [filters, setFilters] = useState(emptyHotelFilters());
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [search, setSearch] = useState(searchParams.get("q") || "");
  const [appliedSearch, setAppliedSearch] = useState(searchParams.get("q") || "");

  function hotelQueryParams(pageNumber) {
    return {
      page: pageNumber,
      limit: PAGE_SIZE,
      cityId: filters.cityId || undefined,
      search: appliedSearch.trim() || undefined,
    };
  }

  useEffect(() => {
    let alive = true;

    async function loadHotels() {
      setLoading(true);
      try {
        const payload = await agentApi.get("/umrah-hotels", {
          params: hotelQueryParams(1),
        });
        if (!alive) return;
        const data = unwrapData(payload);
        const pagination = readPagination(payload);
        const items = asArray(data);
        setHotels(items);
        setPage(1);
        setHasNext(pagination.hasNext);
        setTotal(pagination.total || items.length);
      } catch (fetchError) {
        if (!alive) return;
        setHotels([]);
        setHasNext(false);
        showApiError(fetchError, "LOAD_HOTELS");
      } finally {
        if (alive) setLoading(false);
      }
    }

    loadHotels();

    return () => {
      alive = false;
    };
  }, [filters.cityId, appliedSearch]);

  useEffect(() => {
    const nextQuery = searchParams.get("q") || "";
    setSearch(nextQuery);
    setAppliedSearch(nextQuery);
  }, [searchParams]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setAppliedSearch((current) => {
        const next = search.trim();
        return current === next ? current : next;
      });
    }, 400);

    return () => window.clearTimeout(handle);
  }, [search]);

  function applySearch() {
    setAppliedSearch(search.trim());
  }

  async function loadMore() {
    const nextPage = page + 1;
    setLoadingMore(true);
    try {
      const payload = await agentApi.get("/umrah-hotels", {
        params: hotelQueryParams(nextPage),
      });
      const data = unwrapData(payload);
      const pagination = readPagination(payload);
      const nextHotels = asArray(data);
      setHotels((current) => appendUniqueById(current, nextHotels, (hotel) => hotel.id));
      setPage(nextPage);
      setHasNext(pagination.hasNext);
      setTotal(pagination.total || 0);
    } catch (fetchError) {
      showApiError(fetchError, "LOAD_HOTELS");
    } finally {
      setLoadingMore(false);
    }
  }

  const displayedHotels = useMemo(
    () => hotels.filter((hotel) => hotelMatchesFilters(hotel, filters)),
    [hotels, filters]
  );
  const hasActiveFilters = countHotelFilters(filters) > 0 || Boolean(search.trim());

  return (
    <AgentLayout>
      <div className="container-fluid py-24 package-catalog-page">
        <PageHeader
          title="Umrah Hotels"
          items={[DASHBOARD_CRUMB, { label: "Services" }, { label: "Hotels" }]}
        >
          <HotelFilterBar
            search={search}
            onSearchChange={setSearch}
            onSearchSubmit={applySearch}
            filters={filters}
            onFilterChange={setFilters}
          />
        </PageHeader>

        {loading ? <CatalogSkeletonGrid variant="hotel" count={8} label="Loading live hotels..." /> : null}

        {!loading && displayedHotels.length === 0 ? (
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center py-5">
              <h5 className="mb-2">No hotels found</h5>
              <p className="text-secondary mb-0">
                {hasActiveFilters
                  ? "Try another city, rating, distance or search term."
                  : "Try a different search term or refresh after hotels are published in admin."}
              </p>
            </div>
          </div>
        ) : null}

        {!loading && displayedHotels.length > 0 ? (
          <div className="package-catalog-grid">
            {displayedHotels.map((hotel) => (
              <HotelCard key={hotel.id} hotel={hotel} />
            ))}
          </div>
        ) : null}

        {!loading && hasNext ? (
          <div className="text-center mt-5">
            <button type="button" className="btn btn-outline-primary" disabled={loadingMore} onClick={loadMore}>
              {loadingMore ? "Loading..." : `Load more ${total ? ` (${hotels.length}/${total})` : ""}`}
            </button>
          </div>
        ) : null}
      </div>
    </AgentLayout>
  );
}
